import React from "react";
import { toast } from "sonner";
import { AlertTriangle, Lock } from "lucide-react";

export const coolKey = (gid, lid) => `${gid}|${lid}`;
export const istDayOf = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : "";
export const isNewDac = (e) => !!e.last_sent_dac && e.last_sent_dac !== e.dac_code;

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "2-digit", year: "numeric" })
    : "";
const copy = (v, label) => navigator.clipboard?.writeText(v).then(() => toast.success(`${label} ${v} copied`));

const CoolRow = ({ e, tid, lead }) => (
  <div className="flex flex-wrap items-center gap-2 py-1.5 px-2 bg-slate-500/5 border border-slate-500/30 rounded" data-testid={`${tid}-row`}>
    {lead}
    <span className="text-[11px] text-slate-700 font-mono min-w-[140px]">{e.group_name}</span>
    <button type="button" onClick={() => copy(e.lpg_id, "LPG ID")} data-testid={`${tid}-lpg`} title="Click to copy LPG ID"
      className="px-2 py-0.5 bg-slate-600 text-white text-xs font-mono font-bold rounded hover:bg-slate-700 cursor-pointer">
      {e.lpg_id}
    </button>
    {e.mobile && (
      <button type="button" onClick={() => copy(e.mobile, "Mobile")} data-testid={`${tid}-mobile`} title="Click to copy Mobile"
        className="px-2 py-0.5 bg-slate-500 text-white text-xs font-mono font-bold rounded hover:bg-slate-600 cursor-pointer">
        Mob {e.mobile}
      </button>
    )}
    <span className="px-2 py-0.5 bg-slate-800 text-white text-xs font-mono font-bold rounded">DAC {e.dac_code}</span>
    {isNewDac(e) && (
      <span className="px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded uppercase tracking-wider" data-testid={`${tid}-newdac`}>
        New DAC
      </span>
    )}
    <span className="text-[10px] text-slate-700 italic">
      — sent on <b>{fmt(e.last_sent_at)}</b>
      {e.last_sent_uploader && <> by <b>{e.last_sent_uploader}</b></>}
      {e.last_sent_agency && e.last_sent_agency !== e.group_name && <> · in <b>{e.last_sent_agency}</b></>}
      {isNewDac(e) && <> · earlier DAC <b>{e.last_sent_dac}</b></>}
    </span>
  </div>
);

export const CoolingPanel = ({ rows, coolingDays, lockDays, sendAnyway, onToggle, canForce }) => {
  const isLocked = (e) => lockDays.includes(istDayOf(e.last_sent_at));
  const today = rows.filter(isLocked);
  const older = rows.filter((e) => !isLocked(e));
  const ticked = older.filter((e) => sendAnyway.has(coolKey(e.group_id, e.lpg_id))).length;
  return (
    <div className="bg-slate-500/10 border-2 border-slate-500/60 rounded-md p-4 space-y-3" data-testid="bulk-preview-cooling">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800" data-testid="bulk-cooling-heading">
        <AlertTriangle className="w-4 h-4" />
        Already-sent ({rows.length}) — {coolingDays}-day cooling window me pehle bhej diya gaya tha
      </div>
      {today.length > 0 && (
        <div className="pl-6 space-y-1.5" data-testid="bulk-cool-today-section">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
            <Lock className="w-3.5 h-3.5" /> Aaj hi bheje gaye ({today.length}) — ye dobara nahi jayengi
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1.5">
            {today.map((e, i) => <CoolRow key={coolKey(e.group_id, e.lpg_id)} e={e} tid={`bulk-cool-today-${i}`} />)}
          </div>
        </div>
      )}
      {older.length > 0 && (
        <div className="pl-6 space-y-1.5" data-testid="bulk-cool-older-section">
          <div className="text-xs font-semibold text-slate-700">
            Pehle bheje gaye ({older.length}){canForce ? ` — ${ticked} ticked, sirf ticked row jayegi (fresh)` : " — skip hongi"}
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1.5">
            {older.map((e, i) => {
              const k = coolKey(e.group_id, e.lpg_id);
              const box = canForce && (
                <label className="flex items-center gap-1 text-[11px] font-semibold text-emerald-800 cursor-pointer">
                  <input type="checkbox" checked={sendAnyway.has(k)} onChange={() => onToggle(k)} data-testid={`bulk-send-anyway-${i}`} />
                  Send anyway
                </label>
              );
              return <CoolRow key={k} e={e} tid={`bulk-cool-older-${i}`} lead={box} />;
            })}
          </div>
        </div>
      )}
    </div>
  );
};
